import { Button } from "@/components/ui/button";
import { Search, User, Heart, Menu, ChevronDown, Store, Package, LogOut, Settings, UserCircle, BookOpen, FileText, X, MessageSquare, Bell } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import CartIcon from "@/components/CartIcon";
import BuyerNotificationBell from "@/components/BuyerNotificationBell";
import logo from "@/assets/logo.png";

interface Category {
  id: string;
  name: string;
  slug: string;
}

// Shared category fetcher for React Query
const fetchCategories = async (): Promise<Category[]> => {
  const { data } = await supabase
    .from("categories")
    .select("id, name, slug")
    .order("name");
  return data || [];
};

interface NavigationProps {
  sticky?: boolean;
}

const Navigation = ({ sticky = true }: NavigationProps) => {
  const { isAdmin } = useAdminCheck();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const isMountedRef = useRef(true);

  // Cached categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories-nav'],
    queryFn: fetchCategories,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Cached seller status
  const { data: isSeller = false } = useQuery({
    queryKey: ['seller-status', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("seller_profiles")
        .select("status")
        .eq("user_id", user.id)
        .single();
      return data?.status === "approved";
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  });

  // Cached user profile
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch unread count - with mount guard to prevent memory leaks
  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      if (isMountedRef.current) setUnreadCount(0);
      return;
    }

    const { data: conversations } = await supabase
      .from("conversations")
      .select("id")
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);

    if (!conversations?.length) {
      if (isMountedRef.current) setUnreadCount(0);
      return;
    }

    const conversationIds = conversations.map(c => c.id);

    const { count } = await supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .in("conversation_id", conversationIds)
      .neq("sender_id", user.id)
      .is("read_at", null);

    if (isMountedRef.current) {
      setUnreadCount(count || 0);
    }
  }, [user]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!user) {
      setUnreadCount(0);
      return;
    }

    fetchUnreadCount();

    // Refresh on visibility change instead of Realtime
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchUnreadCount]);

  const handleCategoryClick = (slug: string) => {
    navigate(`/productos?categoria=${slug}`);
  };

  const displayName = userProfile?.full_name || user?.email?.split("@")[0] || "Usuario";

  return (
    <nav className={`hidden lg:block bg-white border-b border-border z-50 ${sticky ? "sticky top-0" : "relative"}`}>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={logo} alt="TicoMarket - Feria de emprendedores" className="h-16 w-auto" />
        </Link>

        <div className="hidden sm:flex items-center gap-2 flex-1 mx-4 sm:mx-6 md:mx-8">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/productos')}
            title="Buscar productos"
          >
            <Search className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1">
                <Menu className="h-4 w-4" />
                Categorías
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto bg-background border border-border shadow-lg z-[100]">
              {categories.map(category => (
                <DropdownMenuItem
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="cursor-pointer"
                >
                  {category.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/seller-handbook">
            <Button variant="ghost" size="sm">
              Manual del Vendedor
            </Button>
          </Link>
          <Link to="/blog">
            <Button variant="ghost" size="sm">
              Blog
            </Button>
          </Link>
          <Link to="/tiendas">
            <Button variant="ghost" size="sm">
              Tiendas
            </Button>
          </Link>
        </div>
        
        <div className="flex items-center space-x-2">
          {isAdmin && (
            <Link to="/admin">
              
            </Link>
          )}
          <Button variant="ghost" size="sm" className="hidden sm:flex" asChild>
            <Link to="/favoritos">
              <Heart className="h-4 w-4 mr-2" />
              Favoritos
            </Link>
          </Button>
          <CartIcon />
          {user && <BuyerNotificationBell />}
          
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <User className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Mi Cuenta</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background border border-border shadow-lg z-[100]">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <UserCircle className="h-5 w-5 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="font-medium">{displayName}</span>
                      <span className="text-xs text-muted-foreground font-normal">Ver tu perfil</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/mis-pedidos" className="flex items-center gap-2 cursor-pointer">
                      <Package className="h-4 w-4" />
                      Mis Compras
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favoritos" className="flex items-center gap-2 cursor-pointer">
                      <Heart className="h-4 w-4" />
                      Favoritos
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/mensajes" className="flex items-center gap-2 cursor-pointer">
                      <MessageSquare className="h-4 w-4" />
                      Mensajes
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-primary text-primary-foreground text-xs font-medium px-2 py-0.5 rounded-full">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  {isSeller && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/shop-manager" className="flex items-center gap-2 cursor-pointer">
                          <Store className="h-4 w-4" />
                          Gestor de Tienda
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/configuracion" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="h-4 w-4" />
                      Configuración de cuenta
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      await supabase.auth.signOut();
                      navigate("/");
                    }}
                    className="flex items-center gap-2 cursor-pointer text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/registro-vendedor">
                <Button variant="tropical" size="sm">
                  Vender
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Link to="/iniciar-sesion">
                <Button variant="ghost" size="sm">
                  <User className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Iniciar Sesión</span>
                </Button>
              </Link>
              <Link to="/registro">
                <Button variant="outline" size="sm">
                  Registrarse
                </Button>
              </Link>
              <Link to="/registro-vendedor">
                <Button variant="tropical" size="sm">
                  Vender
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
